import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import OndeFica from '../componentes/OndeFica';
import CodigoBarras from '../componentes/CodigoBarras';
import GaleriaCheia from '../componentes/GaleriaCheia';
import {
  ajustarEstoque, carrinhosDasPecas, fornecedoresDaPeca, fotosDaPeca, movimentosDaPeca,
  areasDasCalotas, lugaresExtrasDaPeca, obterPeca, produtoFinanceiro, reservadoDaPeca, similaresDaPeca, urlDaFoto,
  type FornecedorDaPeca,
} from '../lib/consultas';
import { brl, dataHoraBR, paraNumero, qtd } from '../lib/formato';
import { dominioDe, urlSegura } from '../lib/link';
import { descontoPct, emPromocao } from '../lib/promocao';
import { mensagemDeErro } from '../lib/supabase';
import { podeGerenciarCatalogo, useSessao } from '../lib/sessao';
import { descreverEndereco } from '../lib/enderecos';
import { lugaresParaMostrar } from '../lib/ondeFica';
import { ondeFicaAArea } from '../lib/marcasDasCalotas';

const ORIGENS: Record<number, string> = {
  0: 'Nacional', 1: 'Importação direta', 2: 'Importação mercado interno',
  3: 'Nacional (import. 40%-70%)', 4: 'Nacional (processos produtivos)',
  5: 'Nacional (import. ≤ 40%)', 6: 'Importação sem similar nacional',
  7: 'Import. mercado interno sem similar', 8: 'Nacional (import. > 70%)',
};

export default function Peca() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { papel } = useSessao();
  const cliente = useQueryClient();
  const gerencia = podeGerenciarCatalogo(papel);
  // A folha "Onde fica", aberta numa aba (o lugar principal ou um "Também em").
  const [abaDaFolha, setAbaDaFolha] = useState<number | null>(null);
  // Peça sem lugar: dono e gerente a põem num lugar pela mesma folha (fase 3).
  const [porNumLugar, setPorNumLugar] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['peca', id],
    queryFn: () => obterPeca(id),
    enabled: Boolean(id),
  });

  // Os OUTROS lugares da peça (092): a calota que também fica no chão.
  const { data: tambemEm } = useQuery({
    queryKey: ['lugares-extras', id],
    queryFn: () => lugaresExtrasDaPeca(id),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
  // Calota (15/10/2026): o lugar é a área da marca, não o gancho gravado.
  const { data: areas } = useQuery({
    queryKey: ['area-da-calota', id],
    queryFn: () => areasDasCalotas([id]),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
  const area = areas?.get(id) ?? null;
  const { data: movimentos } = useQuery({
    queryKey: ['movimentos', id],
    queryFn: () => movimentosDaPeca(id),
    enabled: Boolean(id),
  });

  // Quanto desta peça está prometido em encomendas ATIVAS — de todos os
  // vendedores, não só as deste usuário. Vem por RPC definer justamente por
  // isso: a RLS mostraria ao vendedor só as encomendas dele, e ele venderia a
  // última unidade que o colega separou sem nunca ver a reserva.
  const { data: reservado } = useQuery({
    queryKey: ['reservado', id],
    queryFn: () => reservadoDaPeca(id),
    enabled: Boolean(id),
  });

  // Custo e margem vêm por RPC, não pela linha do produto: quem decide se pode
  // ver é o banco (produto_financeiro checa o papel). A tela só pede quando faz
  // sentido pedir; se o papel não puder, a RPC devolve null e o bloco some.
  const { data: financeiro } = useQuery({
    queryKey: ['peca-financeiro', id],
    queryFn: () => produtoFinanceiro(id),
    enabled: Boolean(id) && (papel === 'dono' || papel === 'gerente'),
  });

  if (isLoading) return <Layout titulo="Peça"><Carregando /></Layout>;
  if (error || !data) {
    return (
      <Layout titulo="Peça">
        <Aviso tipo="erro">{error ? mensagemDeErro(error) : 'Peça não encontrada.'}</Aviso>
      </Layout>
    );
  }

  const { produto, estoque, codigos, aplicacoes } = data;
  const quantidade = estoque?.quantidade ?? 0;
  const semEstoque = quantidade <= 0;
  const noMinimo = !semEstoque && (estoque?.estoque_min ?? 0) > 0 && quantidade <= (estoque?.estoque_min ?? 0);
  // Vendida antes de a entrada ser registrada (migração 015) — dívida de
  // registro que a entrada de mercadoria abate e o inventário zera.
  const pendente = estoque?.pendente_registro ?? 0;
  // Os lugares da peça, o principal primeiro. "loja" e vazio não são lugar.
  const lugares = lugaresParaMostrar(estoque?.local, tambemEm ?? []);
  const abaDe = (codigo: string) => Math.max(0, lugares.findIndex((l) => l.codigo === codigo.trim()));

  return (
    <Layout
      titulo={produto.sku}
      acao={
        gerencia ? (
          <Link
            to={`/peca/${id}/editar`}
            className="border border-edge px-3 py-2 text-xs font-bold hover:border-signal"
          >
            Editar
          </Link>
        ) : null
      }
    >
      <div className="space-y-4">
        {/* Cabeçalho: o que o vendedor precisa em 1 segundo */}
        <div className="cartao p-5">
          {/* Foto à ESQUERDA do nome, como no catálogo dos fornecedores que é
              o guia do dono: a foto identifica a peça tão rápido quanto o
              nome, e no balcão os dois se conferem num olhar só. */}
          <div className="flex items-start gap-4">
            <FotoDaPeca productId={produto.id} />
            <div className="min-w-0">
              <p className="font-mono text-xs uppercase tracking-wider text-dim">
                {produto.marca ?? 'sem marca'}{produto.categoria ? ` · ${produto.categoria}` : ''}
              </p>
              <h2 className="mt-1 text-xl font-black leading-tight">{produto.descricao}</h2>
              {produto.posicao && <p className="mt-1 text-sm text-mute">{produto.posicao}</p>}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <div>
              <p className="rotulo">Local</p>
              {area ? (
                <p className="inline-block rounded-md bg-edge px-2 py-1 text-base font-bold text-paper">Área {area.nome}</p>
              ) : (
                <p
                  className={[
                    'inline-block rounded-md px-2 py-1 font-mono text-base font-bold',
                    estoque?.local ? 'bg-edge text-paper' : 'border border-dashed border-edge text-dim',
                  ].join(' ')}
                >
                  {estoque?.local ?? 'sem local'}
                </p>
              )}
              {/* "Onde fica" (06/10/2026): o mesmo botão da busca e da venda,
                  que abre o mapa por cima da ficha. Antes era um link miúdo,
                  decidido pela planta do ARQUIVO — um lugar criado no editor
                  (só no banco) ficava sem link. Agora quem decide é ter lugar;
                  a folha lê a planta do banco. */}
              {(area || lugares.length > 0) && (
                <button type="button" onClick={() => setAbaDaFolha(0)}
                        className="botao-secundario mt-2 min-h-[44px] w-full px-2 text-sm">
                  Onde fica ›
                </button>
              )}
              {!area && lugares.length === 0 && gerencia && (
                <button type="button" onClick={() => { setPorNumLugar(true); setAbaDaFolha(0); }}
                        className="botao-secundario mt-2 min-h-[44px] w-full px-2 text-sm">
                  Pôr num lugar ›
                </button>
              )}
            </div>
            <div>
              <p className="rotulo">Estoque</p>
              <p className={[
                'text-2xl font-black',
                semEstoque ? 'text-erro' : noMinimo ? 'text-alerta' : 'text-ok',
              ].join(' ')}>
                {qtd(quantidade)}
                <span className="ml-1 text-sm font-normal text-dim">{produto.unidade}</span>
              </p>
              {pendente > 0 && (
                <p className="text-xs font-semibold text-alerta">
                  {qtd(pendente)} vendida(s) sem registro — lançar entrada
                </p>
              )}
              {/* Prometido em encomenda. Fica aqui, colado no saldo, porque é
                  aqui que o vendedor decide se pode vender a última unidade —
                  não numa aba que ele abriria depois de já ter prometido. */}
              {(reservado ?? 0) > 0 && (
                <p className="text-xs font-semibold text-alerta">
                  {qtd(reservado)} separada(s) em encomenda
                </p>
              )}
            </div>
            <div>
              <p className="rotulo">Balcão</p>
              {/* Em promoção o balcão aparece rasurado e quem manda é o
                  promocional — o mesmo preço que a Venda pré-preenche. */}
              {emPromocao(produto) ? (
                <>
                  <p className="text-xs text-dim line-through">{brl(produto.preco_balcao)}</p>
                  <p className="text-2xl font-black text-paper">
                    {brl(produto.preco_promocional)}
                  </p>
                  {(descontoPct(produto) ?? 0) > 0 && (
                    <p className="inline-block rounded-md bg-edge px-1.5 py-0.5 text-xs font-bold text-paper">
                      promoção −{descontoPct(produto)}%
                    </p>
                  )}
                </>
              ) : (
                <p className="text-2xl font-black text-paper">{brl(produto.preco_balcao)}</p>
              )}
            </div>
          </div>

          {/* O endereço dito por extenso (regra da loja, lib/enderecos.ts): quem
              não decorou a regra acha a peça lendo. */}
          {area ? (
            <p className="mt-2 text-sm text-mute">Calotas {area.marca === 'importados' ? 'importadas' : `da ${area.nome}`}: {ondeFicaAArea(area)}</p>
          ) : descreverEndereco(estoque?.local) && (
            <p className="mt-2 text-sm text-mute">{descreverEndereco(estoque?.local)}</p>
          )}
          {(tambemEm ?? []).filter((l) => !area || !/^GC-/i.test(l)).length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-mute">
              <span className="text-dim">Também em:</span>
              {/* Cada lugar abre a folha já na aba dele. */}
              {(tambemEm ?? []).filter((l) => !area || !/^GC-/i.test(l)).map((l) => (
                <button key={l} type="button" onClick={() => setAbaDaFolha(abaDe(l))}
                        className="min-h-[44px] text-left underline decoration-edge underline-offset-4 hover:text-paper">
                  {descreverEndereco(l) ?? l} <span className="font-mono text-xs text-dim">({l})</span>
                </button>
              ))}
            </div>
          )}

          {!produto.cadastro_completo && (
            <p className="mt-4 border border-alerta/40 bg-alerta/10 px-3 py-2 text-xs text-alerta">
              Cadastro incompleto — falta preencher dados fiscais, logísticos ou de aplicação.
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate('/venda', { state: { productId: produto.id } })}
              className="botao-primario flex-1"
            >
              Vender
            </button>
            {gerencia && <AjusteRapido productId={produto.id} atual={quantidade} aoSalvar={() => {
              cliente.invalidateQueries({ queryKey: ['peca', id] });
              cliente.invalidateQueries({ queryKey: ['movimentos', id] });
            }} />}
          </div>
        </div>

        {/* Onde está na loja (06/10/2026): substitui o mapa 2D de store_zones,
            que nunca apareceu (o levantamento das zonas não foi feito). É um
            BOTÃO que abre a folha com o mapa, e não o desenho fixo na página:
            o desenho segura o dedo e a ficha não rolava no iPhone (revisão). */}
        {(area || lugares.length > 0) && (
          <section className="cartao space-y-2 p-4">
            <h3 className="rotulo">Onde está na loja</h3>
            <button type="button" className="botao-secundario min-h-11 w-full justify-between"
                    onClick={() => setAbaDaFolha(0)}>
              {area
                ? <span>Área {area.nome} · {ondeFicaAArea(area)}</span>
                : <span className="font-mono">{lugares[0]!.codigo}</span>}
              <span>Ver no mapa ›</span>
            </button>
          </section>
        )}

        {abaDaFolha !== null && (
          <OndeFica
            aoFechar={() => { setAbaDaFolha(null); setPorNumLugar(false); }}
            abaInicial={abaDaFolha}
            comecarMudando={porNumLugar}
            peca={{
              product_id: produto.id, descricao: produto.descricao, sku: produto.sku, marca: produto.marca,
              quantidade, lugares, area,
            }}
          />
        )}

        {/* Similares logo abaixo do saldo, de propósito: o momento desta lista
            é "a marca que o cliente pediu está em falta" — a alternativa tem
            que aparecer ANTES de o vendedor dizer não. */}
        <Similares grupo={produto.grupo_similar} atualId={produto.id} />

        {/* 1. Identificação */}
        <Grupo titulo="Identificação">
          {/* O código de barras DA CASA, gerado do próprio SKU. Fica aqui porque
              o fluxo do cadastro rápido termina nesta tela: o SKU só existe
              depois de salvar. Bipar isto cai no mesmo caminho de um EAN de
              fábrica — buscar_pecas dá relevância 1.0 para SKU exato. */}
          <div className="flex flex-col items-center gap-1 py-3">
            <CodigoBarras valor={produto.sku} alturaPx={52} moduloPx={2} />
            <p className="text-[0.6875rem] text-dim">
              Código da loja — imprima a etiqueta e a peça passa a ser bipável.
            </p>
          </div>
          <Linha rotulo="Código interno (SKU)" valor={produto.sku} mono />
          <Linha rotulo="Código original (OEM)" valor={produto.codigo_oem} mono />
          <Linha rotulo="Código do fabricante" valor={produto.codigo_fabrica} mono />
          <Linha rotulo="Código de barras (EAN)" valor={produto.codigo_barras} mono />
          <Linha rotulo="Cód. barras do fabricante" valor={produto.codigo_barras_fabricante} mono />
          {codigos.length > 0 && (
            <div className="py-2">
              <p className="rotulo">Equivalências</p>
              <ul className="flex flex-wrap gap-1.5">
                {codigos.map((c) => (
                  <li key={c.id} className="border border-edge px-2 py-1 font-mono text-xs">
                    <span className="text-dim">{c.tipo}</span> {c.valor}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Grupo>

        <NoCarrinhoDeAlguem productId={produto.id} />

        {podeGerenciarCatalogo(papel) && <OndeComprar productId={produto.id} />}

        {/* 2. Aplicação (fitment) */}
        <Grupo titulo="Aplicação">
          {aplicacoes.length > 0 ? (
            <ul className="divide-y divide-edge">
              {aplicacoes.map((a) => (
                <li key={a.id} className="py-2 text-sm">
                  <span className="font-semibold">{a.montadora} {a.modelo}</span>
                  {(a.ano_inicial || a.ano_final) && (
                    <span className="text-mute"> · {a.ano_inicial ?? '?'}–{a.ano_final ?? '?'}</span>
                  )}
                  {a.motorizacao && <span className="text-mute"> · {a.motorizacao}</span>}
                  {a.observacao && <p className="text-xs text-dim">{a.observacao}</p>}
                </li>
              ))}
            </ul>
          ) : produto.aplicacao ? (
            <p className="py-2 text-sm text-mute">{produto.aplicacao}</p>
          ) : (
            <p className="py-2 text-sm text-dim">Nenhuma aplicação cadastrada.</p>
          )}
        </Grupo>

        {/* 3. Estoque */}
        <Grupo titulo="Estoque">
          <Linha rotulo="Localização" valor={estoque?.local} mono />
          <Linha rotulo="Quantidade" valor={qtd(estoque?.quantidade)} />
          {pendente > 0 && <Linha rotulo="Pendente de registro" valor={qtd(pendente)} />}
          {(reservado ?? 0) > 0 && (
            <Linha rotulo="Separado em encomenda" valor={`${qtd(reservado)} un`} />
          )}
          <Linha rotulo="Estoque mínimo" valor={qtd(estoque?.estoque_min)} />
          <Linha rotulo="Ponto de pedido" valor={qtd(estoque?.reorder_point)} />
          <Linha rotulo="Estoque máximo" valor={qtd(estoque?.estoque_max)} />
        </Grupo>

        {/* 4. Preços — custo e margem NÃO vêm mais no produto: são revogados no
             banco para o papel `authenticated` e chegam pela RPC
             produto_financeiro(), que checa o papel do lado de lá. Antes, a
             coluna vinha para todo mundo e só a tela escondia. */}
        <Grupo titulo="Preços">
          {financeiro && <Linha rotulo="Custo" valor={brl(financeiro.preco_custo)} />}
          <Linha rotulo="Balcão" valor={brl(produto.preco_balcao)} />
          {emPromocao(produto) && (
            <Linha
              rotulo={`Promocional (−${descontoPct(produto)}%)`}
              valor={brl(produto.preco_promocional)}
            />
          )}
          {financeiro && <Linha rotulo="E-commerce" valor={brl(financeiro.preco_ecommerce)} />}
          {financeiro?.margem_balcao_pct != null ? (
            <Linha rotulo="Margem balcão" valor={`${Number(financeiro.margem_balcao_pct).toFixed(1)}%`} />
          ) : null}
          {/* A DECIDIDA ao lado da praticada: é a comparação que o estudo de
              margem por produto precisa (migração 029). */}
          {financeiro?.margem_alvo != null ? (
            <Linha rotulo="Margem alvo" valor={`${qtd(Number(financeiro.margem_alvo))}×`} />
          ) : null}
        </Grupo>

        {/* 5. Fiscal */}
        <Grupo titulo="Fiscal">
          <Linha rotulo="NCM" valor={produto.ncm} mono />
          <Linha rotulo="Origem da mercadoria" valor={ORIGENS[produto.origem_mercadoria] ?? null} />
        </Grupo>

        {/* 6. Logística */}
        <Grupo titulo="Logística">
          <Linha rotulo="Peso" valor={produto.peso_kg ? `${qtd(produto.peso_kg)} kg` : null} />
          <Linha
            rotulo="Dimensões (A×L×C)"
            valor={
              produto.altura_cm || produto.largura_cm || produto.comprimento_cm
                ? `${qtd(produto.altura_cm)} × ${qtd(produto.largura_cm)} × ${qtd(produto.comprimento_cm)} cm`
                : null
            }
          />
        </Grupo>

        {produto.detalhes && (
          <Grupo titulo="Para que serve">
            <p className="whitespace-pre-wrap py-2 text-sm">{produto.detalhes}</p>
          </Grupo>
        )}

        {produto.observacoes && (
          <Grupo titulo="Observações do balcão">
            <p className="whitespace-pre-wrap py-2 text-sm text-mute">{produto.observacoes}</p>
          </Grupo>
        )}

        {/* Histórico */}
        <Grupo titulo="Últimos movimentos">
          {movimentos && movimentos.length > 0 ? (
            <ul className="divide-y divide-edge">
              {movimentos.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div>
                    <span className="font-semibold capitalize">{m.reason}</span>
                    {m.observacao && <span className="text-dim"> · {m.observacao}</span>}
                    <p className="text-xs text-dim">{dataHoraBR(m.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <span className={m.delta >= 0 ? 'font-mono text-ok' : 'font-mono text-erro'}>
                      {m.delta >= 0 ? '+' : ''}{qtd(m.delta)}
                    </span>
                    <p className="font-mono text-xs text-dim">saldo {qtd(m.saldo_apos)}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-2 text-sm text-dim">Sem movimentos registrados.</p>
          )}
        </Grupo>
      </div>
    </Layout>
  );
}

function Grupo({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="cartao p-5">
      <h3 className="rotulo">{titulo}</h3>
      <div className="mt-1 divide-y divide-edge">{children}</div>
    </section>
  );
}

function Linha({ rotulo, valor, mono }: { rotulo: string; valor?: string | null; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2 text-sm">
      <span className="text-dim">{rotulo}</span>
      <span className={[mono ? 'font-mono' : '', valor ? '' : 'text-dim'].join(' ')}>
        {valor || '—'}
      </span>
    </div>
  );
}

/** Inventário de balcão: contou, digitou, corrigiu. O delta vai para o histórico. */
function AjusteRapido({
  productId, atual, aoSalvar,
}: { productId: string; atual: number; aoSalvar: () => void }) {
  const [aberto, setAberto] = useState(false);
  const [valor, setValor] = useState(String(atual));
  const [motivo, setMotivo] = useState('');

  const mutacao = useMutation({
    mutationFn: async () => {
      const n = paraNumero(valor);
      if (n === null || n < 0) throw new Error('Quantidade inválida.');
      await ajustarEstoque({
        productId, novaQuantidade: n, motivo: 'inventario',
        observacao: motivo.trim() || null,
      });
    },
    onSuccess: () => { setAberto(false); aoSalvar(); },
  });

  if (!aberto) {
    return (
      <button type="button" onClick={() => setAberto(true)} className="botao-secundario">
        Ajustar estoque
      </button>
    );
  }

  return (
    <div className="w-full space-y-3 border border-edge p-4">
      <p className="rotulo">Quantidade contada agora</p>
      <input
        className="campo text-lg"
        inputMode="decimal"
        value={valor}
        onChange={(e) => setValor(e.target.value)}
        autoFocus
      />
      <input
        className="campo"
        placeholder="Motivo (opcional): inventário, perda, devolução…"
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
      />
      {mutacao.error ? <Aviso tipo="erro">{mensagemDeErro(mutacao.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button
          type="button"
          className="botao-primario flex-1"
          onClick={() => mutacao.mutate()}
          disabled={mutacao.isPending}
        >
          {mutacao.isPending ? 'Salvando…' : 'Confirmar'}
        </button>
        <button type="button" className="botao-secundario" onClick={() => setAberto(false)}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

/**
 * Onde comprar esta peça quando ela acabar.
 *
 * Pedido do dono (10/08/2026): saber, olhando a ficha, quais fornecedores
 * costumam ter — e conseguir ir pesquisar no site ou no WhatsApp deles sem
 * procurar o contato em outro lugar. O vínculo é declarado no cadastro da peça
 * e também nasce sozinho de toda Entrada de mercadoria com fornecedor
 * informado.
 *
 * O CÓDIGO DELE em destaque, e não o nosso SKU: é o que serve para pesquisar
 * fora. O nosso SKU não significa nada no catálogo do outro.
 *
 * Custo aparece só para quem já pode vê-lo em toda parte, e só quando veio de
 * compra real — `custo = 0` aqui quer dizer "vínculo declarado, nunca cotado",
 * e mostrar R$ 0,00 seria mentir de um jeito que estraga decisão de compra.
 */
/**
 * As outras marcas do mesmo grupo (migração 027). Só existe quando a peça tem
 * grupo_similar — e some quando o grupo está vazio, porque um título "Similares"
 * sem linha embaixo só ensinaria a ignorá-lo.
 *
 * Estoque e preço na própria linha: a decisão do balcão ("ofereço o Bosch?") se
 * toma aqui, sem abrir cada ficha para descobrir se tem e quanto custa.
 */
function Similares({ grupo, atualId }: { grupo: string | null; atualId: string }) {
  const lista = useQuery({
    queryKey: ['similares', grupo, atualId],
    queryFn: () => similaresDaPeca(grupo!, atualId),
    enabled: Boolean(grupo),
  });

  const itens = lista.data ?? [];
  if (!grupo || itens.length === 0) return null;

  return (
    <Grupo titulo="Similares (outras marcas)">
      <ul className="divide-y divide-edge">
        {itens.map((s) => (
          <li key={s.id}>
            <Link
              to={`/peca/${s.id}`}
              className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm hover:bg-edge/20"
            >
              <div className="flex min-w-0 items-center gap-2">
                <MiniaturaDaPeca productId={s.id} />
                <div className="min-w-0">
                <span className="font-semibold">{s.marca ?? 'sem marca'}</span>
                {s.codigo_fabrica && (
                  <span className="ml-2 font-mono text-xs text-mute">{s.codigo_fabrica}</span>
                )}
                <span className="ml-2 font-mono text-xs text-dim">{s.sku}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={['font-bold', s.quantidade > 0 ? 'text-ok' : 'text-erro'].join(' ')}>
                  {qtd(s.quantidade)}
                </span>
                <span className="text-mute">{brl(s.preco_promocional ?? s.preco_balcao)}</span>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </Grupo>
  );
}

function OndeComprar({ productId }: { productId: string }) {
  const { papel } = useSessao();
  const lista = useQuery({
    queryKey: ['fornecedores-da-peca', productId],
    queryFn: () => fornecedoresDaPeca(productId),
  });

  const itens = lista.data ?? [];
  if (itens.length === 0) return null;

  return (
    <Grupo titulo="Onde comprar">
      <ul className="divide-y divide-edge">
        {itens.map((v) => <LinhaFornecedor key={v.supplier_id} vinculo={v} papel={papel} />)}
      </ul>
    </Grupo>
  );
}

function LinhaFornecedor({ vinculo, papel }: {
  vinculo: FornecedorDaPeca;
  papel: ReturnType<typeof useSessao>['papel'];
}) {
  const f = vinculo.fornecedor;
  const cotado = Number(vinculo.custo) > 0 && vinculo.ultima_cotacao;

  // Só dígitos: o WhatsApp recusa link com parêntese e hífen do formato local.
  const zap = f.whatsapp?.replace(/\D/g, '');
  const site = urlSegura(f.site);
  // Site primeiro: quando a peça acaba, o caminho é conferir preço e
  // disponibilidade no catálogo ANTES de mandar mensagem.
  const acoes = [
    site ? { href: site, rotulo: dominioDe(site) } : null,
    zap ? { href: `https://wa.me/${zap.length <= 11 ? `55${zap}` : zap}`, rotulo: 'WhatsApp' } : null,
    !zap && f.email ? { href: `mailto:${f.email}`, rotulo: 'E-mail' } : null,
  ].filter((a): a is { href: string; rotulo: string } => a !== null);

  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
      <div className="min-w-0">
        <span className="font-semibold">{f.nome}</span>
        {!f.ativo && <span className="ml-2 text-xs text-dim">(inativo)</span>}
        {vinculo.codigo_fornecedor ? (
          <span className="ml-2 font-mono text-xs">
            <span className="text-dim">cód. dele</span> {vinculo.codigo_fornecedor}
          </span>
        ) : (
          <span className="ml-2 text-xs text-dim">sem o código dele</span>
        )}
        {podeVerFinancasOuGerencia(papel) && cotado && (
          <p className="text-xs text-mute tabular-nums">
            última cotação {brl(Number(vinculo.custo))} em {dataHoraBR(vinculo.ultima_cotacao!)}
          </p>
        )}
      </div>
      {acoes.length > 0 && (
        <div className="flex shrink-0 flex-wrap gap-1.5">
          {acoes.map((a) => (
            <a key={a.rotulo} href={a.href} target="_blank" rel="noreferrer"
               className="botao-terciario">
              {a.rotulo}
            </a>
          ))}
        </div>
      )}
    </li>
  );
}

/** Custo é de gerente e dono — a mesma régua da ficha financeira acima. */
const podeVerFinancasOuGerencia = (p: ReturnType<typeof useSessao>['papel']) =>
  p === 'gerente' || p === 'dono';

/**
 * A reserva de balcão na ficha: quem está com esta peça no carrinho AGORA
 * (migração 026). Some sozinha em até 30 min — por isso a consulta se renova
 * a cada 15 s enquanto a ficha está aberta.
 */
function NoCarrinhoDeAlguem({ productId }: { productId: string }) {
  const { session } = useSessao();
  const meuId = session?.user.id ?? null;
  const carrinhos = useQuery({
    queryKey: ['carrinhos-da-peca', productId],
    queryFn: () => carrinhosDasPecas([productId]),
    refetchInterval: 15_000,
  });
  const linhas = carrinhos.data ?? [];
  if (linhas.length === 0) return null;

  return (
    <div className="border border-alerta/40 bg-alerta/5 p-3">
      {linhas.map((c) => (
        <p key={c.vendedor_id} className="text-sm text-alerta">
          {c.vendedor_id === meuId ? 'No seu carrinho: ' : `${c.vendedor} reservou `}
          {qtd(c.quantidade)} un{c.cliente_nome ? ` para ${c.cliente_nome}` : ''} —
          a reserva expira sozinha em até 30 min.
        </p>
      ))}
    </div>
  );
}

/**
 * A galeria no cabeçalho da ficha. A PRINCIPAL aparece; havendo mais, um toque
 * abre a tela cheia e dá para passar para o lado — dedo (swipe) ou setas.
 * Só aparece quando existe foto: um quadrado "sem foto" no lugar mais nobre da
 * tela cobraria de todo mundo um preenchimento que é opcional.
 */
function FotoDaPeca({ productId }: { productId: string }) {
  const galeria = useQuery({
    queryKey: ['fotos', productId],
    queryFn: () => fotosDaPeca(productId),
  });
  const [aberta, setAberta] = useState(false);
  const fotos = galeria.data ?? [];
  if (fotos.length === 0) return null;

  const principal = fotos[0]!;
  return (
    <>
      <button type="button" onClick={() => setAberta(true)}
              className="block shrink-0 text-left" aria-label="Abrir as fotos da peça">
        {/* 30% menor que a versão de estreia (224px → 160px) e presa à
            esquerda do nome: é guia visual, não pôster. */}
        <img
          src={urlDaFoto(principal.id, principal.updated_at)}
          alt="Foto da peça"
          className="max-h-40 max-w-40 border border-edge object-contain"
        />
        {fotos.length > 1 && (
          <span className="mt-1 block text-center text-xs text-dim">
            1 de {fotos.length}
          </span>
        )}
      </button>
      {aberta && (
        <GaleriaCheia fotos={fotos} urlDe={(f) => urlDaFoto(f.id, f.updated_at)}
                      aoFechar={() => setAberta(false)} />
      )}
    </>
  );
}

/** Miniatura para a lista de similares — a PRINCIPAL; some quando não há foto. */
function MiniaturaDaPeca({ productId }: { productId: string }) {
  const galeria = useQuery({
    queryKey: ['fotos', productId],
    queryFn: () => fotosDaPeca(productId),
  });
  const principal = galeria.data?.[0];
  if (!principal) return null;
  return (
    <img
      src={urlDaFoto(principal.id, principal.updated_at)}
      alt=""
      className="h-10 w-10 shrink-0 border border-edge object-cover"
    />
  );
}
